import { format, startOfMonth, endOfMonth } from "date-fns";
import { useMemo, useState } from "react";
import { useIsPortraitMobile } from "../../../hooks/useIsPortraiteMobile";
import TiltMessage from "../../../components/feedback/TiltMessage";
import { SectionHeader } from "../../SectionHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { WholesaleSalesCard } from "../components/wholesale-salesCard";
import { WholesaleSalesByTime } from "../components/wholesale-salesByTime";
import { WholesaleCategoryPerformance } from "../components/wholesale-categoryPerformance";
import { WholesaleSalesDataTable } from "../components/wholesale-salesDataTable";
import { MonthYearPicker } from "../../ui/month-Year-Picker";
import { useAccountingWholesale } from "../hooks/useAccountingWholesale";
import { useWholesaleSales } from "../hooks/useSalesWholesale";
import { useWholesaleDataCheck } from "../hooks/useWholesaleDataCheck";
import { FileWarning, TriangleAlert } from "lucide-react";
/* import { WholesaleTopProducts } from "../components/wholesale-topSelling"; */



export function WholesaleSales() {
    const now = new Date();
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date(now.getFullYear(), now.getMonth()));
    const formattedDate = selectedDate ? format(selectedDate, "yyyy-MM") : "";
    const startDate = format(startOfMonth(new Date(selectedDate || now)), "yyyy-MM-dd");
    const endDate = format(endOfMonth(new Date(selectedDate || now)), "yyyy-MM-dd");

    const { data: accountingData } = useAccountingWholesale("month");
    const { data: salesData } = useWholesaleSales(startDate, endDate);

    const revenueData = accountingData?.revenue_overview;
    const wholesaleCategoryData = salesData?.wholesale_category_performance || [];
    const showTiltMessage = useIsPortraitMobile();

    // Use the combined data check hook
    const { hasDataForSelectedMonth: hasRevenueData, hasAnyData: hasRevenueAnyData } = useWholesaleDataCheck(
        revenueData,
        { selectedMonth: formattedDate }
    );
    const { hasAnyData: hasCategoryData } = useWholesaleDataCheck(wholesaleCategoryData);
    const hasDataForSelectedMonth = hasRevenueData && hasCategoryData && hasRevenueAnyData && hasCategoryData;
    const isCurrentMonthIncomplete = useMemo(() => {
        if (!selectedDate) return false;
        const selectedMonthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const selectedMonthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
        return now >= selectedMonthStart && now <= selectedMonthEnd && now.getDate() < selectedMonthEnd.getDate();
    }, [selectedDate, now]);

    return (
        <div className="space-y-8">
            <TiltMessage show={showTiltMessage} />
            <div className={showTiltMessage ? "blur-sm pointer-events-none" : ""}>
                <SectionHeader title="Sales and Product Reports" />
                <Tabs defaultValue="overview">
                    <div className="flex flex-row items-center justify-between w-full">
                        <div className="mb-4">
                            <MonthYearPicker selected={selectedDate} onChange={setSelectedDate} />
                        </div>
                        <div>
                            {!hasDataForSelectedMonth && (
                                <div className="text-sm text-red-600 bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft px-3 py-2 mb-4 flex flex-row items-center">
                                    <FileWarning /> Missing file uploads for the selected month.
                                </div>
                            )}
                            {isCurrentMonthIncomplete && (
                                <div className="text-sm text-yellow-200 bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft px-3 py-2 flex flex-row items-center">
                                    <TriangleAlert /> The current month is incomplete.
                                </div>
                            )}
                        </div>
                    </div>
                    <TabsList className="m-0">
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="transactions">Transactions</TabsTrigger>
                    </TabsList>
                    <TabsContent
                        value="overview"
                        className="grid grid-cols-6 gap-x-8 w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible"
                    >
                        <div className="col-span-6 h-full flex flex-col">
                            <WholesaleSalesCard selectedMonth={formattedDate} />
                        </div>
                        <div className="col-span-6 h-full flex flex-col">
                            <WholesaleSalesByTime />
                        </div>
                        <div className="col-span-6 h-full flex flex-col">
                            <WholesaleCategoryPerformance selectedMonth={formattedDate} />
                        </div>
                    </TabsContent>
                    <TabsContent
                        value="transactions"
                        className="grid grid-cols-6 gap-x-8 w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible"
                    >
                        <div className="col-span-6 h-full flex flex-col">
                            <WholesaleSalesDataTable />
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}